import React from "react";
import { motion } from "framer-motion";
import { Camera, FileText, ShieldCheck, ClipboardCheck } from "lucide-react";

const features = [
  {
    title: "Smart Reporting System",
    description:
      "Our guards create incident reports instantly with photos, details, and timestamps. Reports are securely stored in our client portal, allowing managers to access, track, and print them anytime.",
    icon: <FileText className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Body & Vehicle Camera Integration",
    description:
      "High-definition body and dash cameras provide accurate evidence and accountability during every shift. Video footage is securely stored, ensuring transparency for both clients and staff.",
    icon: <Camera className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Real-Time Quality Monitoring",
    description:
      "With our guard management system, supervisors monitor patrol activity, response times, and service quality in real time. This ensures consistent, reliable protection for every client site.",
    icon: <ShieldCheck className="w-8 h-8 text-red-600" />,
  },
  {
    title: "Standardized Operating Procedures",
    description:
      "The New Way Security follows strict, updated SOPs across all operations. This guarantees compliance, maximizes efficiency, and ensures every client receives the highest level of service.",
    icon: <ClipboardCheck className="w-8 h-8 text-red-600" />,
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.2,
      duration: 0.6,
      ease: "easeOut",
    },
  }),
};

const TechnologySection = () => {
  return (
    <section className="py-16 px-4 md:px-10 lg:px-20">
      <div className="max-w-6xl mx-auto">
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-gray-800 border-b-4 border-red-600 inline-block mb-12"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Technology
        </motion.h2>

        <motion.p
          className="text-gray-700 text-lg mb-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.6 }}
        >
          At <strong>The New Way Security</strong>, we combine advanced digital
          tools with field expertise to strengthen safety and accountability.
          From real-time monitoring to seamless reporting, our technology
          empowers guards and reassures clients.
        </motion.p>

        <motion.p
          className="text-gray-700 text-lg mb-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          We embrace smart, paperless systems that enhance transparency and
          efficiency. Every security officer is equipped with tools that ensure
          faster response, clear communication, and greater reliability on duty.
        </motion.p>

        <div className="grid md:grid-cols-2 gap-10">
          {features.map((feature, i) => (
            <motion.div
              key={feature.title}
              className="bg-white shadow-md rounded-2xl p-6 flex gap-4"
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div>{feature.icon}</div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800 mb-1">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-base leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechnologySection;
